import React, { useState } from 'react';
import { ArrowLeft, ShoppingBag, Swords } from 'lucide-react';
import { PetInventory } from './inventory/PetInventory';
import { ItemInventory } from './inventory/ItemInventory';
import { LoadingSpinner } from './ui/LoadingSpinner';
import type { User } from '../types/user';

interface InventoryPageProps {
  onBack: () => void;
  user: User | null;
}

export function InventoryPage({ onBack, user }: InventoryPageProps) {
  const [activeTab, setActiveTab] = useState<'pets' | 'items'>('pets');
  const [isLoading, setIsLoading] = useState(true);

  // Simulate initial loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-amber-900 text-amber-100 p-8 bg-[url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&q=80')] bg-cover bg-center bg-blend-overlay">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Kingdom
        </button>

        <div className="text-center mb-12">
          <div className="flex justify-center gap-3 mb-4">
            <ShoppingBag className="w-12 h-12 text-yellow-500" />
          </div>
          <h1 className="text-4xl font-bold font-serif mb-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-transparent bg-clip-text">
            Royal Treasury
          </h1>
          <p className="text-xl text-amber-200 font-serif">
            Your collection of mystical creatures and enchanted artifacts
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="flex gap-4 mb-8">
              <button
                onClick={() => setActiveTab('pets')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-serif
                  ${activeTab === 'pets' 
                    ? 'bg-gradient-to-r from-yellow-600 to-amber-500 text-white' 
                    : 'bg-gray-800 text-amber-200 hover:bg-gray-700'}`}
              >
                <Swords className="w-5 h-5" />
                Battle Pets
              </button>
              <button
                onClick={() => setActiveTab('items')}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-serif
                  ${activeTab === 'items'
                    ? 'bg-gradient-to-r from-yellow-600 to-amber-500 text-white'
                    : 'bg-gray-800 text-amber-200 hover:bg-gray-700'}`}
              >
                <ShoppingBag className="w-5 h-5" />
                Items
              </button>
            </div>

            <div className="bg-gray-900/80 rounded-xl p-6 shadow-xl border border-amber-500/20">
              {activeTab === 'pets' ? (
                <PetInventory user={user} />
              ) : (
                <ItemInventory user={user} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}